import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt,
  FaLandmark,
  FaClock,
} from "react-icons/fa";

import "./Ujjain.css";

const Ujjain = () => {
  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: "smooth",
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const itineraryData = [
    {
      day: 1,
      title: "Indore Sightseeing & Cultural Walk",
      points: [
        "Arrival in Indore and hotel check-in.",
        "Visit Rajwada Palace.",
        "Explore Lal Bagh Palace.",
        "Enjoy evening food walk at Sarafa Bazaar.",
        "Experience Indore’s local culture and cuisine.",
      ],
      icon: <FaLandmark />,
    },

    {
      day: 2,
      title: "Indore to Ujjain | Temple Circuit & River Ghats",
      points: [
        "Drive from Indore to Ujjain.",
        "Darshan at Mahakaleshwar Jyotirlinga.",
        "Visit Kal Bhairav Temple.",
        "Visit Harsiddhi Temple.",
        "Holy experience at Ram Ghat.",
        "Overnight stay in Ujjain.",
      ],
      icon: <FaLandmark />,
    },

    {
      day: 3,
      title: "Bhasma Aarti, Omkareshwar Excursion & Return",
      points: [
        "Attend sacred Bhasma Aarti at Mahakaleshwar Temple.",
        "Drive towards Omkareshwar.",
        "Darshan at Omkareshwar Jyotirlinga.",
        "Visit Mamleshwar Temple.",
        "Return journey to Indore.",
      ],
      icon: <FaClock />,
    },
  ];

  return (
    <div className="trip-container">

      {/* HEADER */}
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >

        <div className="header-left">

          <h1 className="trip-title">
            Ujjain & Omkareshwar Jyotirlinga
          </h1>

          <p className="trip-duration">
            2 Nights / 3 Days
          </p>

        </div>

        <div className="header-right">

          <div className="info-row">
            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>

              <p>
                Indore • Ujjain • Omkareshwar • Mahakaleshwar
              </p>
            </div>
          </div>

          <div className="info-row">
            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Start & End:</span>
              <p>Indore</p>
            </div>
          </div>

          <div className="info-row type-tag">
            <FaGlobeAmericas className="header-icon" />
            <p>Spiritual • Temple • Heritage • Pilgrimage</p>
          </div>

        </div>
      </motion.header>

      {/* IMAGE GRID */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.unsplash.com/photo-1658730458768-8b8cc0c00955?q=80&w=327&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Mahakaleshwar Temple"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://images.unsplash.com/photo-1658730487395-dcc99f5d997c?q=80&w=327&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Temple"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1638814175187-bef04bac8f6e?q=80&w=1032&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Omkareshwar"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1775585571702-ef8da2c1edcf?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Spiritual Journey"
            />
          </div>

        </div>
      </motion.div>

      {/* STICKY NAV */}
      <nav className="sticky-nav">

        <button
          className="tab-link"
          onClick={() => scrollToSection(itineraryRef)}
        >
          Detailed Itinerary
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(highlightsRef)}
        >
          Highlights
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(inclusionsRef)}
        >
          Policy & Inclusions
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(aboutRef)}
        >
          About Trip
        </button>

      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}
        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Tour Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (
              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">

                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>

                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}

                </div>
              </motion.div>
            ))}

          </div>
        </section>

        {/* HIGHLIGHTS */}
        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Darshan at Mahakaleshwar Jyotirlinga</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Attend sacred Bhasma Aarti</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit Kal Bhairav Temple</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Holy experience at Ram Ghat</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Darshan at Omkareshwar Jyotirlinga</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Explore Sarafa Bazaar food market</p>
            </div>

          </div>
        </motion.section>

        {/* INCLUSIONS */}
        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaCheckCircle />
              What's Included
            </h4>

            <ul className="p-list">

              <li>
                <FaUtensils />
                Lunch and dinner
              </li>

              <li>
                <FaHotel />
                Accommodation for 2 nights
              </li>

              <li>
                <FaBus />
                All transfers and sightseeing
              </li>

              <li>
                <FaCheckCircle />
                Driver charges, fuel, toll taxes and parking
              </li>

              <li>
                <FaShieldAlt />
                Temple visit planning assistance
              </li>

            </ul>
          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">
              <FaTimesCircle />
              What's Excluded
            </h4>

            <ul className="p-list">

              <li>
                <FaTimesCircle />
                Bhasma Aarti registration charges
              </li>

              <li>
                <FaTimesCircle />
                Entry fees for monuments
              </li>

              <li>
                <FaUtensils />
                Meals not specifically mentioned
              </li>

              <li>
                <FaTimesCircle />
                Personal expenses and shopping
              </li>

              <li>
                <FaShieldAlt />
                Travel insurance and emergency costs
              </li>

              <li>
                <FaTimesCircle />
                Additional expenses due to unforeseen circumstances
              </li>

            </ul>
          </div>

        </motion.section>

        {/* ABOUT */}
        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">
            This spiritually enriching journey covers two of the most sacred
            Jyotirlinga shrines of Madhya Pradesh — Mahakaleshwar in Ujjain
            and Omkareshwar along the serene banks of the Narmada River.
            <br /><br />
            Designed for comfortable travel and meaningful darshan experiences,
            the itinerary balances temple visits, cultural exploration, and
            relaxed travel pacing. From the divine Bhasma Aarti at
            Mahakaleshwar Temple to the peaceful atmosphere of Omkareshwar,
            this pilgrimage offers a deeply spiritual and memorable experience.
          </p>

        </motion.section>

        {/* TERMS */}
        <div className="accordion-item">

          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />

          </Link>
        </div>

        {/* PRIVACY */}
        <div className="accordion-item">

          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />

          </Link>
        </div>

      </div>
    </div>
  );
};

export default Ujjain;